import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useIntl } from '@/react-intl';
import BaseApi from '@/uptk/core/api/BaseApi';
import { Button } from '@/design-system/ds-0/components/Button';
import { Modal } from '@/design-system/ds-0/components/Modal';
import { getAuthContextState, subscribeAuthContextState } from '@/uptk/core/api/auth_context_store';
import StudioCheckbox from '../shared/StudioCheckbox';
import { invalidateStudioState } from '../data/studioMutationFeedback';

const MAX_BYTES = 16 * 1024 * 1024;
type Report = { protectionDecisions?: { transported: number; ignored: number; remaining: number }; dossierCode: string; reused: boolean; created: string[]; ignored: string[]; moduleDefinition: string };
export default function StudioPortDialog({ mode, dossierId = '', onClose, onImported }: {
  mode: 'import' | 'export'; dossierId?: string; onClose: () => void; onImported: () => void;
}) {
  const intl = useIntl();
  const identity = useSyncExternalStore(subscribeAuthContextState, getAuthContextState, () => null);
  const scope = `${identity?.tenantId ?? ''}:${identity?.userId ?? ''}`;
  const initialScope = useRef(scope);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { if (scope !== initialScope.current) onClose(); }, [scope, onClose]);
  const current = () => {
    const now = getAuthContextState();
    return mounted.current && `${now?.tenantId ?? ''}:${now?.userId ?? ''}` === initialScope.current;
  };
  const t = (key: string) => intl.formatMessage({ id: ['moduleStudio', 'port', key].join('.') });
  const [file, setFile] = useState<File | null>(null);
  const [code, setCode] = useState(dossierId);
  const [name, setName] = useState('');
  const [versions, setVersions] = useState('');
  const [rename, setRename] = useState(false);
  const [moduleCode, setModuleCode] = useState('');
  const [privacy, setPrivacy] = useState(false);
  const [rights, setRights] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState<Report | null>(null);
  const [exported, setExported] = useState(false);
  const run = async () => {
    if (busy || !privacy || !rights) return;
    setBusy(true); setError('');
    try {
      if (mode === 'export') {
        const selected = versions.trim() ? versions.split(',').map(value => Number(value.trim())) : null;
        if (selected?.some(value => !Number.isInteger(value) || value < 0)) throw new Error(t('versionsInvalid'));
        const response = await BaseApi.post('/api/v1/module-studio/dossiers/' + encodeURIComponent(dossierId) + '/commands/export-dossier', {
          confirmed: true, requestId: crypto.randomUUID(), parameters: { input: { versions: selected, attestationNoPersonalData: privacy, attestationRights: rights } },
        });
        if (!current()) return;
        const blob = new Blob([JSON.stringify(response.data)], { type: 'application/json' });
        if (blob.size > MAX_BYTES) throw new Error(t('tooLarge'));
        const url = URL.createObjectURL(blob);
        try { const link = document.createElement('a'); link.href = url; link.download = dossierId + '-studio.json'; link.click(); }
        finally { URL.revokeObjectURL(url); }
        setExported(true); invalidateStudioState();
      } else {
        if (!file || file.size > MAX_BYTES) throw new Error(t('tooLarge'));
        const bytes = new Uint8Array(await file.arrayBuffer());
        if (!current()) return;
        let binary = '';
        for (let i = 0; i < bytes.length; i += 8192) binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
        const response = await BaseApi.post('/api/v1/module-studio/commands/import-dossier', {
          confirmed: true, requestId: crypto.randomUUID(), parameters: {
            archive: btoa(binary), input: { dossierCode: code, name, moduleCode, renameModule: rename, attestationNoPersonalData: privacy, attestationRights: rights },
          },
        });
        if (!current()) return;
        setReport(response.data as Report); invalidateStudioState(); onImported();
      }
    } catch (cause) {
      if (!current()) return;
      const response = cause as { response?: { data?: { code?: string; detail?: string; message?: string } } };
      const detail = response.response?.data;
      setError(detail?.code ?? detail?.detail ?? detail?.message ?? (cause instanceof Error ? cause.message : t('failed')));
    } finally { if (current()) setBusy(false); }
  };
  if (scope !== initialScope.current) return null;
  return <Modal open onClose={() => { if (!busy) onClose(); }} title={t(mode)} closeLabel={t('close')}>
    <p>{t('explanation')}</p>
    {error ? <p role="alert">{t('failed')} {error}</p> : null}
    {exported ? <p role="status">{intl.formatMessage({ id: 'moduleStudio.port.exported' })}</p> : report ? <div role="status">
      <p>{report.dossierCode}: {t(report.reused ? 'reused' : 'complete')}</p>
      <p>{t(report.moduleDefinition.startsWith('linked:') ? 'linked' : 'definitionProposed')} {report.moduleDefinition.split(':').slice(1).join(':')}</p>
      {report.protectionDecisions ? <p>{intl.formatMessage({ id: 'moduleStudio.port.protectionCounts' }, report.protectionDecisions)}</p> : null}
      <h3>{t('created')}</h3><ul>{report.created.map(item => <li key={item}>{t(item.startsWith('dossier:') ? 'dossier' : item.startsWith('version:') ? 'version' : item.startsWith('prototype:') ? 'prototype' : 'testplan')} {item.split(':')[1]}</li>)}</ul>
      <h3>{t('ignored')}</h3><ul>{report.ignored.map(item => <li key={item}>{item.startsWith('testplan:') ? t('collision') + ' ' + item.split(':')[1] : t('excluded')}</li>)}</ul>
    </div> : <>
      {mode === 'import' ? <>
        <label>{t('file')}<input type="file" accept="application/json,.json" disabled={busy} onChange={event => setFile(event.target.files?.[0] ?? null)} /></label>
        <label>{t('code')}<input value={code} disabled={busy} onChange={event => setCode(event.target.value)} /></label>
        <label>{t('name')}<input value={name} disabled={busy} onChange={event => setName(event.target.value)} /></label>
        <StudioCheckbox checked={rename} disabled={busy} onChange={event => setRename(event.target.checked)}>{t('rename')}</StudioCheckbox>
        {rename ? <label>{t('moduleCode')}<input value={moduleCode} disabled={busy} onChange={event => setModuleCode(event.target.value)} /></label> : null}
      </> : <label>{t('versions')}<input value={versions} disabled={busy} onChange={event => setVersions(event.target.value)} /></label>}
      <StudioCheckbox checked={privacy} disabled={busy} onChange={event => setPrivacy(event.target.checked)}>{t('privacy')}</StudioCheckbox>
      <StudioCheckbox checked={rights} disabled={busy} onChange={event => setRights(event.target.checked)}>{t('rights')}</StudioCheckbox>
      <Button disabled={busy || !privacy || !rights || mode === 'import' && (!file || !code || rename && !moduleCode)} onClick={() => void run()}>{t(mode)}</Button>
    </>}
  </Modal>;
}
